import { Extension } from "@tiptap/core"
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { findGhostMentionMatch, mentionMatchLabel } from "./ghost-mention-match"
import { shouldInsertSpaceAfterMention } from "./mention-insert-space"
import { isMentionSuggestionActive } from "./suggestion"
import { mentionAttrsFromItem, type MentionItem } from "./types"

export type InlineMentionCompletionOptions = {
  getItems: () => MentionItem[]
  getOnMentionInsert: () => ((item: MentionItem) => void) | undefined
}

export type GhostMatchState = {
  from: number
  to: number
  item: MentionItem
  suffix: string
}

const WORD_BEFORE_CURSOR_RE = /([\p{L}\p{N}_-]+)$/u

export function computeGhostMatch(state: EditorState, items: MentionItem[]): GhostMatchState | null {
  if (isMentionSuggestionActive()) return null
  const { empty, from } = state.selection
  if (!empty) return null

  const $from = state.selection.$from
  const parentStart = $from.start()
  const textBefore = state.doc.textBetween(parentStart, from, "\n", "\0")
  const wordMatch = textBefore.match(WORD_BEFORE_CURSOR_RE)
  if (!wordMatch) return null

  const prefix = wordMatch[1] ?? ""
  if (prefix.length < 2) return null

  const prefixStartInText = textBefore.length - prefix.length
  if (prefixStartInText > 0 && textBefore[prefixStartInText - 1] === "@") return null

  const item = findGhostMentionMatch(prefix, items)
  if (!item) return null

  const matchLabel = mentionMatchLabel(item)
  const suffix = matchLabel.slice(prefix.length)

  return {
    from: parentStart + prefixStartInText,
    to: from,
    item,
    suffix,
  }
}

function ghostSuffixDom(suffix: string): HTMLSpanElement {
  const span = document.createElement("span")
  span.className = "pe-ghost-completion"
  span.textContent = suffix
  span.setAttribute("aria-hidden", "true")
  return span
}

const ghostPluginKey = new PluginKey<GhostMatchState | null>("inlineMentionCompletion")

/**
 * Ghost-autocomplete по префиксу имени сущности (≥2 символа).
 * Tab → mention-чип; Escape сбрасывает; не активен при открытом @-меню.
 */
export const InlineMentionCompletion = Extension.create<InlineMentionCompletionOptions>({
  name: "inlineMentionCompletion",

  addOptions() {
    return {
      getItems: () => [],
      getOnMentionInsert: () => undefined,
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    const getItems = () => this.options.getItems()
    const getOnMentionInsert = () => this.options.getOnMentionInsert()

    return [
      new Plugin<GhostMatchState | null>({
        key: ghostPluginKey,
        state: {
          init: () => null,
          apply(tr, _value, _oldState, newState) {
            if (tr.getMeta(ghostPluginKey) === "clear") return null
            return computeGhostMatch(newState, getItems())
          },
        },
        props: {
          decorations(state) {
            const ghost = ghostPluginKey.getState(state)
            // Exact full-name match: no grey suffix, but Tab still converts via plugin state.
            if (!ghost?.suffix) return null
            return DecorationSet.create(state.doc, [
              Decoration.widget(ghost.to, () => ghostSuffixDom(ghost.suffix), {
                side: 1,
                key: `ghost:${ghost.suffix}`,
              }),
            ])
          },
          handleKeyDown(view, event) {
            const ghost = ghostPluginKey.getState(view.state)
            if (!ghost) return false

            if (event.key === "Tab" && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
              event.preventDefault()
              const item = ghost.item
              const attrs = mentionAttrsFromItem(item)
              const insertSpace = shouldInsertSpaceAfterMention(view.state.doc, ghost.to)
              editor
                .chain()
                .focus()
                .deleteRange({ from: ghost.from, to: ghost.to })
                .insertContent([
                  { type: "mention", attrs },
                  ...(insertSpace ? [{ type: "text" as const, text: " " }] : []),
                ])
                .run()
              getOnMentionInsert()?.(item)
              return true
            }

            if (event.key === "Escape") {
              view.dispatch(view.state.tr.setMeta(ghostPluginKey, "clear"))
              return true
            }

            return false
          },
        },
      }),
    ]
  },
})
